import React from 'react'; 
import { 
  Card, 
  CardHeader, 
  CardContent, 
  CardActions, 
  Typography, 
  Button, 
  IconButton,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'; 
import { format } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';

const SafeValue = (value, fallback = 'N/A', formatter = null) => {
  if (value === null || value === undefined) return fallback;
  return formatter ? formatter(value) : value;
};

const InvoiceCard = ({ 
  invoice = {}, 
  onDownloadPDF = () => {}, 
  onEditInvoice = () => {}, 
  onDeleteInvoice = () => {}, 
  onShareInvoice = () => {} 
}) => {
  
  const billBy = invoice.billBy || {};
  const billTo = invoice.billTo || {};
  const items = invoice.items || [];

  return (
    <Card>
      <CardHeader
        title={`Invoice: ${SafeValue(invoice.invoiceId)}`}
        subheader={`Created: ${SafeValue(
          invoice.currentDate, 
          'N/A', 
          date => format(new Date(date), 'MM/dd/yyyy HH:mm')
        )} | Due: ${SafeValue(
          invoice.dueDate, 
          'N/A', 
          date => format(new Date(date), 'MM/dd/yyyy')
        )}`}
        action={
          <div className="flex space-x-2">
            <IconButton onClick={() => onEditInvoice(invoice)}>
              <EditIcon color="primary" />
            </IconButton>
            <IconButton onClick={() => onShareInvoice(invoice)}>
              <ShareIcon color="secondary" />
            </IconButton>
            <IconButton onClick={() => onDeleteInvoice(invoice)}>
              <DeleteIcon color="error" />
            </IconButton>
          </div>
        }
      />
      <CardContent>
        <Grid container spacing={3}>
         
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Billed By
            </Typography>
            <Paper elevation={0} className="p-4 bg-gray-50">
              <Typography variant="body1" fontWeight="fontWeightBold">
                {SafeValue(billBy.name)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                GST No: {SafeValue(billBy.gstNo)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Email: {SafeValue(billBy.email)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Address: {SafeValue(billBy.address)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Billed To
            </Typography>
            <Paper elevation={0} className="p-4 bg-gray-50">
              <Typography variant="body1" fontWeight="fontWeightBold">
                {SafeValue(billTo.name)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Address: {SafeValue(billTo.address)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Email: {SafeValue(billTo.email)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Phone: {SafeValue(billTo.phoneNo)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                GST No: {SafeValue(billTo.gstNo)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Invoice Items
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item Description</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">GST</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length > 0 ? (
                    items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          {SafeValue(item.description, `Item ${index + 1}`)}
                        </TableCell>
                        <TableCell align="right">
                          {SafeValue(item.qty)}
                        </TableCell>
                        <TableCell align="right">
                          {SafeValue(item.unit)}
                        </TableCell>
                        <TableCell align="right">
                        ₹ {SafeValue(item.rate)}
                        </TableCell>
                        <TableCell align="right">
                          {SafeValue(item.Discount)}%
                        </TableCell>
                        <TableCell align="right">
                          {SafeValue(item.gst)}%
                        </TableCell>
                        <TableCell align="right">
                          {SafeValue(
                            item.total, 
                            'N/A', 
                            total => `₹${total.toFixed(2)}`
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No items found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} className="p-4 bg-gray-50">
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                </Grid>
                <Grid item xs={6} style={{ textAlign: 'right' }}>
                  <Typography variant="body2">
                    {SafeValue(
                      invoice.subtotal, 
                      'N/A', 
                      total => `₹${total.toFixed(2)}`
                    )}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Global Discount
                  </Typography>
                </Grid>
                <Grid item xs={6} style={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="error">
                    {SafeValue(
                      invoice.globalDiscountAmount, 
                      'N/A', 
                      amount => `-₹${amount.toFixed(2)}`
                    )}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="h6">Total Amount</Typography>
                </Grid>
                <Grid item xs={6} style={{ textAlign: 'right' }}>
                  <Typography variant="h6" color="primary">
                    {SafeValue(
                      invoice.total, 
                      'N/A', 
                      total => `₹${total.toFixed(2)}`
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {invoice.comment && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Comments:</strong> {invoice.comment}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>Status:</strong> {SafeValue(invoice.status)}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
      
      <CardActions>
        <Button
          sx={{color:"white", backgroundColor:"red"}}
          fullWidth
          variant="contained"
          onClick={() => onDownloadPDF(invoice)}
        >
          Download PDF
        </Button>
      </CardActions>
    </Card>
  );
};

export default InvoiceCard;